export const KAIROS_PATIENT_TIMELINE_RECOMMENDED_PRIOR = 'KAIROS_PATIENT_TIMELINE_RECOMMENDED_PRIOR';

const PREFERRED_MODALITY_ORDER = ['CT', 'MR', 'MRI', 'PT', 'PET', 'NM', 'US', 'MG', 'CR', 'DX', 'XR', 'XA', 'OT'];

export const normalizePriorModalities = (modalities = '') => String(modalities || '')
  .split(/[\s,/]+/)
  .map(m => m.trim().toUpperCase())
  .filter(Boolean)
  .map(m => (m === 'MRI' ? 'MR' : m === 'PET' ? 'PT' : m));

export const getPriorYear = (prior = {}) => {
  const raw = String(prior.studyDate || prior.study_date || '').trim();
  if (/^\d{8}$/.test(raw)) return raw.slice(0, 4);
  if (/^\d{4}/.test(raw)) return raw.slice(0, 4);
  return 'Unknown';
};

export const formatPriorStudyDateTime = (dateStr, timeStr = '') => {
  const rawDate = String(dateStr || '').trim();
  if (!rawDate || rawDate.length < 8) return '—';
  const d = `${rawDate.slice(0, 4)}-${rawDate.slice(4, 6)}-${rawDate.slice(6, 8)}`;
  const rawTime = String(timeStr || '').trim();
  if (rawTime && rawTime.length >= 4) {
    return `${d} ${rawTime.slice(0, 2)}:${rawTime.slice(2, 4)}`;
  }
  return d;
};

const modalityRank = (modality) => {
  const idx = PREFERRED_MODALITY_ORDER.indexOf(String(modality || '').trim().toUpperCase());
  return idx === -1 ? PREFERRED_MODALITY_ORDER.length : idx;
};

const studySortKey = (prior = {}) => `${prior.studyDate || prior.study_date || ''}${prior.studyTime || prior.study_time || ''}`;

export const buildPriorTimeline = (priors = []) => {
  const cells = new Map();
  const yearsSet = new Set();
  const modalitiesSet = new Set();

  priors.forEach((prior) => {
    const year = getPriorYear(prior);
    const modalities = normalizePriorModalities(prior.modalities || prior.modality || 'OT');
    const safeModalities = modalities.length ? modalities : ['OT'];
    yearsSet.add(year);
    safeModalities.forEach((modality) => {
      modalitiesSet.add(modality);
      const key = `${modality}:${year}`;
      const next = cells.get(key) || [];
      next.push(prior);
      cells.set(key, next);
    });
  });

  const years = Array.from(yearsSet).sort((a, b) => {
    if (a === 'Unknown') return 1;
    if (b === 'Unknown') return -1;
    return Number(a) - Number(b);
  });
  const modalities = Array.from(modalitiesSet).sort((a, b) => {
    const byRank = modalityRank(a) - modalityRank(b);
    return byRank || a.localeCompare(b);
  });

  cells.forEach(studies => studies.sort((a, b) => studySortKey(b).localeCompare(studySortKey(a))));
  return { years, modalities, cells };
};


const parseDicomDateValue = (value) => {
  const raw = String(value || '').trim();
  const match = raw.match(/^(\d{4})(\d{2})(\d{2})/);
  if (/^\d+$/.test(raw) && !match) return null;
  if (match) {
    const date = Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
    return Number.isFinite(date) ? date : null;
  }
  const parsed = Date.parse(raw);
  return Number.isFinite(parsed) ? parsed : null;
};

const normalizedTokens = (value) => String(value || '')
  .toLowerCase()
  .split(/[^a-z0-9]+/)
  .map(token => token.trim())
  .filter(token => token.length > 2 && !['with', 'without', 'and', 'the', 'for', 'follow', 'up'].includes(token));

const tokenOverlapScore = (left, right) => {
  const a = new Set(normalizedTokens(left));
  const b = new Set(normalizedTokens(right));
  if (!a.size || !b.size) return 0;
  let hits = 0;
  a.forEach(token => { if (b.has(token)) hits += 1; });
  return hits / Math.max(a.size, b.size);
};

const firstPresent = (study, keys) => {
  for (const key of keys) {
    const value = study?.[key];
    if (value !== undefined && value !== null && String(value).trim()) return value;
  }
  return '';
};

const studyUidValue = (study = {}) => firstPresent(study, ['studyInstanceUID', 'study_instance_uid', 'StudyInstanceUID']);
const studyDateValue = (study = {}) => firstPresent(study, ['studyDate', 'study_date', 'StudyDate']);
const studyDescriptionValue = (study = {}) => firstPresent(study, ['studyDescription', 'study_description', 'StudyDescription', 'description']);
const bodyPartValue = (study = {}) => firstPresent(study, ['bodyPartExamined', 'body_part_examined', 'body_part', 'BodyPartExamined']);

export const scorePriorMatch = (prior = {}, currentStudyInfo = {}) => {
  const tags = [];
  let score = 0;
  const currentModalities = normalizePriorModalities(firstPresent(currentStudyInfo, ['modalities', 'modality', 'Modality']));
  const priorModalities = normalizePriorModalities(firstPresent(prior, ['modalities', 'modality', 'Modality']));
  if (currentModalities.some(modality => priorModalities.includes(modality))) {
    score += 40;
    tags.push('SAME MODALITY');
  }

  // Keep body-part matching conservative for Phase 1; fuzzy DICOM synonyms can be added after live inventory review.
  const currentBodyPart = String(bodyPartValue(currentStudyInfo)).trim().toUpperCase();
  const priorBodyPart = String(bodyPartValue(prior)).trim().toUpperCase();
  if (currentBodyPart && priorBodyPart && currentBodyPart === priorBodyPart) {
    score += 25;
    tags.push('SAME BODY PART');
  }

  const descriptionOverlap = tokenOverlapScore(studyDescriptionValue(currentStudyInfo), studyDescriptionValue(prior));
  if (descriptionOverlap >= 0.3) {
    score += Math.round(15 * descriptionOverlap);
    tags.push('LONGITUDINAL');
  }

  const currentDate = parseDicomDateValue(studyDateValue(currentStudyInfo));
  const priorDate = parseDicomDateValue(studyDateValue(prior));
  if (currentDate && priorDate && priorDate < currentDate) {
    const days = Math.max(1, (currentDate - priorDate) / 86400000);
    score += Math.max(1, Math.round(20 * Math.min(1, 365 / days)));
    if (days <= 183) tags.push('RECENT');
  }

  return { score, tags: Array.from(new Set(tags)) };
};

export const getRecommendedPriors = (priors = [], currentStudyInfo = null) => {
  if (!Array.isArray(priors) || !priors.length || !currentStudyInfo) return [];
  const currentUid = studyUidValue(currentStudyInfo);
  return priors
    .map(prior => ({ study: prior, ...scorePriorMatch(prior, currentStudyInfo) }))
    .filter(item => studyUidValue(item.study) && studyUidValue(item.study) !== currentUid && item.score > 0 && item.tags.length > 0)
    .sort((a, b) => b.score - a.score || studySortKey(b.study).localeCompare(studySortKey(a.study)));
};

export const getRecommendedPrior = (priors = [], currentStudyInfo = null) => getRecommendedPriors(priors, currentStudyInfo)[0] || null;

export const getPriorTimelineCell = (timeline, modality, year) => (
  timeline?.cells?.get(`${modality}:${year}`) || []
);

export const getPriorTimelineSelection = (timeline, start, end) => {
  if (!timeline || !start || !end) return [];
  const yearA = timeline.years.indexOf(start.year);
  const yearB = timeline.years.indexOf(end.year);
  const modalityA = timeline.modalities.indexOf(start.modality);
  const modalityB = timeline.modalities.indexOf(end.modality);
  if (yearA < 0 || yearB < 0 || modalityA < 0 || modalityB < 0) return [];
  const y0 = Math.min(yearA, yearB);
  const y1 = Math.max(yearA, yearB);
  const m0 = Math.min(modalityA, modalityB);
  const m1 = Math.max(modalityA, modalityB);
  const seen = new Set();
  const selected = [];
  for (let mi = m0; mi <= m1; mi += 1) {
    for (let yi = y0; yi <= y1; yi += 1) {
      getPriorTimelineCell(timeline, timeline.modalities[mi], timeline.years[yi]).forEach((prior) => {
        const uid = prior.studyInstanceUID || prior.study_instance_uid;
        if (uid && !seen.has(uid)) {
          seen.add(uid);
          selected.push(prior);
        }
      });
    }
  }
  return selected.sort((a, b) => studySortKey(b).localeCompare(studySortKey(a)));
};
